import {
    ChangeDetectionStrategy,
    Component,
    DestroyRef,
    InputSignal,
    OutputEmitterRef,
    Signal,
    WritableSignal,
    computed,
    inject,
    input,
    output,
    signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';

import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';

import { BlockDirective, ConcatClassesPipe, ElemDirective, ModDirective } from '@rt-tools/core';
import { NotificationBus, RtButtonDirective, RtTooltipDirective } from '@rt-tools/ui-kit-v2';
import { FeedbackFormComponent, InfoIconComponent, ToggleComponent } from '@stair-craft/common/components';
import {
    CONFIG_CONSTRAINTS,
    DEFAULT_STAIR_CONFIG,
    IConfigConstraints,
    IStairCalculationResult,
    IStairConfig,
    IStairType,
    IWoodMaterial,
    SHOW_MATERIAL_COST,
    WOOD_MATERIALS,
    buildTypeSwitchConfig,
} from '@stair-craft/common/util';

import { PARAM_HINT_TEXTS } from './param-hint-texts.const';
import { readStairConfigFile, stairConfigLoadErrorMessage } from './stair-config-file.loader';

export interface IParamChangeEvent {
    field: string;
    value: unknown;
}

const PARAM_CHANGE_DEBOUNCE_MS: number = 800;
const BEM_BLOCK: string = 'sc-params-panel';

@Component({
    selector: 'sc-params-panel',
    templateUrl: './params-panel.component.html',
    styleUrl: './params-panel.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        // angular
        FormsModule,
        RouterLink,

        // rt-tools
        BlockDirective,
        ElemDirective,
        ConcatClassesPipe,
        ModDirective,
        FeedbackFormComponent,
        InfoIconComponent,
        ToggleComponent,

        // rt-tools ui-kit
        RtButtonDirective,
        RtTooltipDirective,
    ],
    host: {
        class: BEM_BLOCK,
    },
})
export class ParamsPanelComponent {
    readonly #destroyRef: DestroyRef = inject(DestroyRef);
    readonly #notifications: NotificationBus = inject(NotificationBus);
    readonly #paramChangesSource: Subject<IParamChangeEvent> = new Subject<IParamChangeEvent>();

    protected readonly Math: Math = Math;
    protected readonly articleDescriptions: Readonly<Record<string, string>> = PARAM_HINT_TEXTS;
    protected readonly dropActive: WritableSignal<boolean> = signal<boolean>(false);
    protected readonly showCost: boolean = SHOW_MATERIAL_COST;
    protected readonly materials: IWoodMaterial[] = WOOD_MATERIALS;
    protected readonly constraints: IConfigConstraints = CONFIG_CONSTRAINTS;
    protected readonly blogOverviewText: string = $localize`:@@hintBlogOverview:Справочник по типам лестниц и параметрам конструктора. Подробное описание каждого размера и нормы — в разделе блога.`;
    protected readonly stairTypes: { value: IStairType; label: string }[] = [
        { value: 'straight' as const, label: $localize`:@@stairTypeStraight:Прямая` },
        { value: 'l-shape' as const, label: $localize`:@@stairTypeLShape:Г-образная` },
        { value: 'u-shape' as const, label: $localize`:@@stairTypeUShape:П-образная` },
        { value: 'spiral' as const, label: $localize`:@@stairTypeSpiral:Винтовая` },
    ];

    protected readonly activeStairTypeLabel: Signal<string> = computed(
        (): string =>
            this.stairTypes.find((stairType: { value: IStairType; label: string }): boolean => stairType.value === this.config().type)
                ?.label ?? ''
    );

    // Display-value for "ступеней до поворота" — falls back to auto-computed flight1Count when null
    protected readonly turnAtStepDisplay: Signal<number | null> = computed(
        (): number | null => this.config().turnAtStep ?? this.result()?.flight1Count ?? null
    );

    // Stair angle in input MUST match the value shown in the calculations summary.
    // result.stairAngle is the actual angle of the rendered geometry (after force-fit etc.) —
    // bind input to it so they're always identical.
    protected readonly stairAngleDisplay: Signal<number | null> = computed(
        (): number | null => this.result()?.stairAngle ?? this.config().stairAngle
    );

    // Fixed cap so the thumb position doesn't drift when the result's stepCount
    // shifts (e.g. user changes flightGap and the layout regenerates). The
    // calculator still clamps the actual flight1Count to whatever the layout
    // can fit; this max only bounds the slider input. 30 covers any realistic
    // residential configuration.
    protected readonly turnAtStepMax: number = 30;

    // Angle constraint: straight stair gets the wide range; L/U-shape gets a tighter range
    // to keep treadDepth near norm even when the stair must reach the opening edge.
    protected readonly angleConstraint: Signal<{ min: number; max: number; step: number }> = computed(
        (): { min: number; max: number; step: number } => {
            const t: IStairConfig['type'] = this.config().type;
            return t === 'l-shape' || t === 'u-shape' ? this.constraints.stair.angleLUShape : this.constraints.stair.angle;
        }
    );

    public readonly config: InputSignal<IStairConfig> = input<IStairConfig>(DEFAULT_STAIR_CONFIG);
    public readonly result: InputSignal<IStairCalculationResult | null> = input<IStairCalculationResult | null>(null);
    // When false, the duplicated blocks (save/load actions, dropzone, results,
    // validation, feedback footer) are hidden — used on wide screens where the
    // right aside renders them instead.
    public readonly showExtras: InputSignal<boolean> = input<boolean>(true);
    public readonly justCopied: InputSignal<boolean> = input<boolean>(false);
    public readonly configChange: OutputEmitterRef<IStairConfig> = output<IStairConfig>();
    public readonly configReset: OutputEmitterRef<void> = output();
    public readonly exportRequested: OutputEmitterRef<void> = output();
    public readonly dxfRequested: OutputEmitterRef<void> = output();
    public readonly partsRequested: OutputEmitterRef<void> = output();
    public readonly shareRequested: OutputEmitterRef<void> = output();
    public readonly paramChanged: OutputEmitterRef<IParamChangeEvent> = output<IParamChangeEvent>();
    public readonly feedbackOpened: OutputEmitterRef<void> = output();
    public readonly feedbackSubmitted: OutputEmitterRef<void> = output();
    public readonly feedbackFailed: OutputEmitterRef<{ reason: string }> = output<{ reason: string }>();

    constructor() {
        this.#paramChangesSource
            .pipe(debounceTime(PARAM_CHANGE_DEBOUNCE_MS), takeUntilDestroyed(this.#destroyRef))
            .subscribe((event: IParamChangeEvent): void => {
                this.paramChanged.emit(event);
            });
    }

    protected stepFromBar(event: MouseEvent, direction: -1 | 1): void {
        const trigger: HTMLElement = event.currentTarget as HTMLElement;
        const bar: HTMLElement | null = trigger.parentElement;
        const input: HTMLInputElement | null | undefined = bar?.querySelector<HTMLInputElement>('input[type="range"]');
        if (!input || input.disabled) {
            return;
        }
        if (direction > 0) {
            input.stepUp();
        } else {
            input.stepDown();
        }
        // Dispatch a single input event. The range ngModel accessor listens to both
        // input and change, so dispatching both fires ngModelChange twice; the second
        // emit reads a not-yet-propagated config and reverts the opening anchor the
        // first emit just applied.
        input.dispatchEvent(new Event('input', { bubbles: true }));
    }

    protected onFileSelected(event: Event): void {
        const picker: HTMLInputElement = event.target as HTMLInputElement;
        const file: File | undefined = picker.files?.[0];
        if (file) {
            this.#loadFile(file);
        }
        picker.value = '';
    }

    protected onDrop(event: DragEvent): void {
        event.preventDefault();
        this.dropActive.set(false);
        const file: File | undefined = event.dataTransfer?.files[0];
        // A name that is not `.json` is refused by the loader with a message, not in silence.
        if (file) {
            this.#loadFile(file);
        }
    }

    protected onDragOver(event: DragEvent): void {
        event.preventDefault();
        this.dropActive.set(true);
    }

    protected onDragLeave(): void {
        this.dropActive.set(false);
    }

    protected resetConfig(): void {
        this.configReset.emit();
    }

    protected setType(type: IStairConfig['type']): void {
        this.configChange.emit(buildTypeSwitchConfig(this.config(), type));
    }

    protected clampRailingOnBlur(field: keyof IStairConfig['railing']): void {
        const c: IStairConfig = this.config();
        const raw: IStairConfig['railing'][keyof IStairConfig['railing']] = c.railing[field];
        if (typeof raw === 'number') {
            const clamped: number = this.#clampRailingField(field, raw);
            if (clamped !== raw) {
                this.configChange.emit({ ...c, railing: { ...c.railing, [field]: clamped } });
            }
        }
    }

    // Emit raw intent. All cross-field clamping/anchoring now lives in the
    // config-normalization seam behind the session, so the panel never repairs
    // the config itself — it just reports what the user touched.
    protected emitField<K extends keyof IStairConfig>(field: K, value: IStairConfig[K]): void {
        if (typeof value === 'number' && !Number.isFinite(value)) {
            return;
        }
        if (value === null && typeof this.config()[field] === 'number') {
            return;
        }

        const c: IStairConfig = this.config();
        this.configChange.emit({ ...c, [field]: value, railing: { ...c.railing } });
        this.#paramChangesSource.next({ field, value });
    }

    protected emitSpiralOpeningDiameter(diameter: number): void {
        if (!Number.isFinite(diameter)) {
            return;
        }
        const c: IStairConfig = this.config();
        this.configChange.emit({ ...c, openingWidth: diameter, openingLength: diameter, railing: { ...c.railing } });
    }

    protected emitRailingField<K extends keyof IStairConfig['railing']>(field: K, value: IStairConfig['railing'][K]): void {
        if (typeof value === 'number' && !Number.isFinite(value)) {
            return;
        }

        const c: IStairConfig = this.config();
        if (typeof value === 'number') {
            value = this.#clampRailingField(field, value) as IStairConfig['railing'][K];
        }
        const railing: IStairConfig['railing'] = { ...c.railing, [field]: value };
        if (field === 'enabled' && value === true) {
            railing.upperEnabled = true;
        }
        this.configChange.emit({ ...c, railing });
        this.#paramChangesSource.next({ field: `railing.${field as string}`, value });
    }

    protected emitDisplayY(displayY: number): void {
        if (!Number.isFinite(displayY)) {
            return;
        }
        const c: IStairConfig = this.config();
        const openingZ: number = Math.max(0, c.roomLength - displayY - c.openingLength);
        this.configChange.emit({ ...c, openingZ, railing: { ...c.railing } });
    }

    #loadFile(file: File): void {
        readStairConfigFile(file).then(
            (loaded: IStairConfig): void => {
                this.configChange.emit(loaded);
            },
            (error: unknown): void => {
                this.#notifications.error(stairConfigLoadErrorMessage(error));
            }
        );
    }

    #clampRailingField(field: string, value: number): number {
        const cn: IConfigConstraints = this.constraints;
        const ranges: Record<string, { min: number; max: number }> = {
            height: cn.railing.height,
            handrailWidth: cn.railing.handrailWidth,
            balusterWidth: cn.railing.balusterWidth,
            balusterSpacing: cn.railing.balusterSpacing,
            postWidth: cn.railing.postWidth,
        };
        const range: { min: number; max: number } | undefined = Object.hasOwn(ranges, field) ? ranges[field] : undefined;
        if (!range) {
            return value;
        }

        return Math.max(range.min, Math.min(range.max, value));
    }
}
